import { LitElement, css, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { WowFormController } from "ui-common";
import { resolve } from "ui-common/di";
import type { IAuthState, IAuthService } from "ui-common/services/auth/types";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/divider/divider";
import "ui-components/creator-tile";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/social-login-buttons/apple-login-button";
import "ui-components/social-login-buttons/facebook-login-button";
import "ui-components/social-login-buttons/google-login-button";
import "ui-components/wow-link/wow-link";
import "ui-components/wow-logo/wow-logo";

import "@phosphor-icons/webcomponents/PhCircleNotch";

import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "helpers/legal-urls";
import { navigateTo } from "../router";
import { t, translate } from "../translations/i18n";
import { isFan } from "helpers/minimalViableProfile";
import { isValidRedirectUrl } from "helpers/url";
import { processPendingCheckout } from "ui-components/process-stripe-checkout";
import { getTheme, setTheme, getResolvedTheme, type Theme } from "ui-common/theme";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
  }

  /* Base layout utilities */
  .container,
  .main-content,
  .header,
  .title-section,
  .form-section,
  .footer {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .main-content {
    gap: var(--sds-size-space-800);
  }

  .header,
  .title-section {
    gap: var(--sds-size-space-800);
  }

  .title-section {
    gap: var(--sds-size-space-400);
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
  }

  .subtitle,
  .terms-text,
  .login-link {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
  }

  .subtitle {
    font-size: var(--sds-typography-scale-03);
    line-height: 1.4;
    color: var(--sds-color-text-default-secondary);
  }

  .terms-text {
    font-size: var(--sds-typography-scale-01);
    line-height: 1.5;
    color: var(--sds-color-text-default-tertiary);
    max-width: 345px;
  }

  .login-link {
    font-size: var(--sds-typography-scale-02);
    line-height: 1.4;
    text-align: center;
    text-decoration: none;
  }

  .login-link:hover {
    text-decoration: underline;
  }

  .social-buttons {
    display: flex;
    gap: 11.57px;
    align-items: center;
  }

  .form-section {
    gap: var(--sds-size-space-600);
  }

  .input-field {
    background-color: var(--sds-color-background-default-secondary);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    border-radius: var(--sds-size-radius-400);
    padding: var(--sds-size-space-400) var(--sds-size-space-600);
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-03) / 1 var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
    width: 100%;
    box-sizing: border-box;
  }

  .input-field::placeholder {
    color: var(--sds-color-text-default-tertiary);
  }

  .input-field:focus {
    outline: none;
    border-color: var(--sds-color-border-default-secondary);
    background-color: var(--sds-color-background-default-default-hover);
  }

  .submit-button {
    display: grid;
  }

  .footer {
    gap: var(--sds-size-space-1200);
    margin-top: var(--sds-size-space-800);
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  form {
    display: contents;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Glass variant styles */
  .glass-page {
    min-height: 100dvh;
    position: relative;
    overflow: hidden;

    & .logo {
      z-index: 1500;
      position: relative;
      width: 393px;
      margin: var(--sds-size-space-800) auto;
      padding: 0 var(--sds-size-space-800);
    }
  }

  .page-background {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .page-background::after {
    --local-visible-width: calc(350px * 0.5);
    --local-shade-width: calc(95px + var(--local-visible-width));

    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      var(--sds-color-background-default-default) calc(50% - var(--local-shade-width)),
      transparent calc(50% - var(--local-visible-width)),
      transparent calc(50% + var(--local-visible-width)),
      var(--sds-color-background-default-default) calc(50% + var(--local-shade-width))
    );

    pointer-events: none;
    z-index: 1;
  }

  .background-tiles {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-300);
    transform: rotate(19.2deg);
    min-width: calc(834px + 300px);
  }

  .tile-row {
    display: flex;
    gap: var(--sds-size-space-300);
  }

  .glass-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
    box-sizing: border-box;
  }

  .glass-modal-container {
    background-color: rgb(0 0 0 / 0.2);
    backdrop-filter: blur(10px);
    border: 2px solid rgb(255 255 255 / 0.1);
    border-radius: var(--sds-size-radius-600);
    max-width: 393px;
    width: 100%;
    padding: var(--sds-size-space-800);
    box-sizing: border-box;
    max-height: 90vh;
    overflow-y: auto;
  }

  .glass-modal-container .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .glass-modal-container .main-content {
    gap: var(--sds-size-space-800);
  }

  .glass-modal-container .footer {
    gap: var(--sds-size-space-1200);
    margin-top: var(--sds-size-space-800);
  }

  @media (max-width: 768px) {
    .glass-modal-container {
      max-height: calc(100vh - 32px);
      padding: var(--sds-size-space-600);
    }
  }
`;

interface SignInFormData {
  email: string;
  password: string;
}

@customElement("sign-in-page")
export class SignInPage extends LitElement {
  static override styles = [styles];

  private creators = [
    [
      {
        handle: "luna.nieuwboer",
        image:
          "https://worldofwow.com/assets/profile_pictures/391a9c87-8195-4b37-a74e-2defd4e0289d/e545877c-d794-4bfe-a5ac-aa935ce97e45.jpg",
      },
      {
        handle: "sannevanlierop",
        image:
          "https://worldofwow.com/assets/profile_pictures/aa87f93a-bdb0-4511-ad48-e986b7a8ef7a/bc09e21e-4831-43cd-b013-b64804959d6f.jpg",
      },
      {
        handle: "bamifit",
        image:
          "https://worldofwow.com/assets/profile_pictures/bea20a5d-c5f6-4361-a87a-6dedfef1e5f1/61b5c2fc-93aa-4652-b0cd-a4c05214bbf6.jpg",
      },
      {
        handle: "thibo.jb",
        image:
          "https://worldofwow.com/assets/profile_pictures/623a1461-3f53-473a-abb2-628cb846d4c4/e608207d-992d-4201-a66d-399613b656b7.jpeg",
      },
      {
        handle: "saraverwoerd",
        image:
          "https://worldofwow.com/assets/profile_pictures/8f1aa8c0-0a00-41a8-9692-0adfbbe467c2/fd8cdc57-3373-45f0-bfa7-6ba7139b3577.jpeg",
      },
      {
        handle: "chefkeluc",
        image:
          "https://worldofwow.com/assets/806bf6a9-47ad-4954-9766-9af7d1b2ba7c/0199706f-9485-7000-9aee-b1a1a0f4c930.avif",
      },
    ],
    [
      {
        handle: "sarahliebregts",
        image:
          "https://worldofwow.com/assets/profile_pictures/ea03465d-b8e5-4b20-835e-1365ebcad7da/4069e008-b0f3-449c-8259-7791aa820b1e.jpg",
      },
      {
        handle: "kalvijn",
        image:
          "https://worldofwow.com/assets/profile_pictures/138e96d1-8345-4b0d-bdf9-6ce7e159f50a/e13ceb6a-a965-45ab-bb9a-27b5c85a607b.jpg",
      },
      {
        handle: "nienkeplas",
        image:
          "https://worldofwow.com/assets/profile_pictures/35faa870-59f2-413d-bf62-d3cdc88d07d2/2f1db09b-9bb5-462d-a62d-2ba037e87874.jpg",
      },
      {
        handle: "ruudgullit",
        image:
          "https://worldofwow.com/assets/profile_pictures/e126adb8-342a-4d59-bf8c-ea682b4eaa33/587fc431-6d88-42f6-8263-37b05d0f77ed.jpg",
      },
      {
        handle: "giel",
        image:
          "https://worldofwow.com/assets/profile_pictures/ff26d71a-35b4-4511-bbaa-77d822366927/b615a172-65f3-40b2-ac73-06a91cc62cf1.jpg",
      },
      {
        handle: "ricoverhoeven",
        image:
          "https://worldofwow.com/assets/profile_pictures/6a40703f-13a3-4110-aae3-fc9fb209a272/64df9b27-200a-4c69-bfe3-e07a1f8f1a6c.jpg",
      },
    ],
    [
      {
        handle: "bizzey",
        image:
          "https://worldofwow.com/assets/profile_pictures/f8353979-4f10-4958-bcb5-bbba3c088881/457508ad-1710-4176-93ef-746e6d725771.jpg",
      },
      {
        handle: "channahkoerten",
        image:
          "https://worldofwow.com/assets/profile_pictures/ab8283b5-e34f-4a68-9a57-2632c7ef4224/45961bd1-17bc-4c0f-a91d-230a5b5766c4.jpg",
      },
      {
        handle: "franklammers",
        image:
          "https://worldofwow.com/assets/f65f5f05-a1fd-464c-928b-2b7977b15364/019b13b4-bca9-7000-81ff-b39ffb2cd59d.avif",
      },
      {
        handle: "lisamichels",
        image:
          "https://worldofwow.com/assets/profile_pictures/1a3c83ca-0a1c-42e6-981e-b3db6dd6035b/75ed8d2d-9943-4cc8-b894-5a2eaffa4f50.jpg",
      },
      {
        handle: "timorworld",
        image:
          "https://worldofwow.com/assets/profile_pictures/3f2d5c92-d3b3-4e1e-8530-2644aec2c5bd/0dacee2b-35b5-4d24-91f1-e476c38d749a.jpg",
      },
      {
        handle: "robbievdgraaf",
        image:
          "https://worldofwow.com/assets/profile_pictures/71e66b89-34c1-4318-a342-b3ef9d32db6f/3eef616e-43fb-42b1-9a52-8923da521659.jpg",
      },
    ],
    [
      {
        handle: "vinchnzo",
        image:
          "https://worldofwow.com/assets/profile_pictures/b2628e09-203d-43d3-b6e9-5bb1aadd728a/127a372c-fbce-4659-96d0-371b9ddc62e1.jpg",
      },
      {
        handle: "simonkeizer",
        image:
          "https://worldofwow.com/assets/a014b51d-0681-43a4-8f89-b665a0ded11e/019adbf5-acb9-7000-aa3f-e2e9d5c66ddc.avif",
      },
      {
        handle: "isabelvangelder",
        image:
          "https://worldofwow.com/assets/profile_pictures/a3e5212b-5dba-43eb-b30e-df0088e7e973/da595277-f273-4a23-a399-0b62d937a716.jpg",
      },
      {
        handle: "jelkavanhouten",
        image:
          "https://worldofwow.com/assets/profile_pictures/dd74f3c2-f654-4e06-aa8e-6b3342d6c99f/717ae2c0-8f4b-42c1-a306-e08aa59b5360.jpg",
      },
      {
        handle: "nordinblessing",
        image:
          "https://worldofwow.com/assets/profile_pictures/991be607-e81f-40bb-88f1-3bf7ec3e2b51/7634ccba-4e7e-4557-82ec-a1cd05c007b0.jpg",
      },
      {
        handle: "joyccee.nl",
        image:
          "https://worldofwow.com/assets/profile_pictures/63406d16-b280-4f48-aa7a-b5306d722ca7/b24a04d3-1a5e-405b-8c67-cbf013c9eb29.jpeg",
      },
    ],
    [
      {
        handle: "giel",
        image:
          "https://worldofwow.com/assets/profile_pictures/ff26d71a-35b4-4511-bbaa-77d822366927/b615a172-65f3-40b2-ac73-06a91cc62cf1.jpg",
      },
      {
        handle: "nienkeplas",
        image:
          "https://worldofwow.com/assets/profile_pictures/35faa870-59f2-413d-bf62-d3cdc88d07d2/2f1db09b-9bb5-462d-a62d-2ba037e87874.jpg",
      },
      {
        handle: "ricoverhoeven",
        image:
          "https://worldofwow.com/assets/profile_pictures/6a40703f-13a3-4110-aae3-fc9fb209a272/64df9b27-200a-4c69-bfe3-e07a1f8f1a6c.jpg",
      },
      {
        handle: "saraverwoerd",
        image:
          "https://worldofwow.com/assets/profile_pictures/8f1aa8c0-0a00-41a8-9692-0adfbbe467c2/fd8cdc57-3373-45f0-bfa7-6ba7139b3577.jpeg",
      },
      {
        handle: "kalvijn",
        image:
          "https://worldofwow.com/assets/profile_pictures/138e96d1-8345-4b0d-bdf9-6ce7e159f50a/e13ceb6a-a965-45ab-bb9a-27b5c85a607b.jpg",
      },
      {
        handle: "lisamichels",
        image:
          "https://worldofwow.com/assets/profile_pictures/1a3c83ca-0a1c-42e6-981e-b3db6dd6035b/75ed8d2d-9943-4cc8-b894-5a2eaffa4f50.jpg",
      },
    ],
  ];

  @state()
  private formError = "";

  @state()
  private isProcessingCheckout = false;

  @state()
  private checkoutError = "";

  @state()
  private isFormInitialized = false;

  @state()
  private isPurchaseMode = false;

  private isGlassVariant = false;

  private originalTheme: Theme | null = null;
  private hasThemeBeenForced = false;

  override connectedCallback() {
    super.connectedCallback();

    // Cache glass variant status from URL params on mount
    const urlParams = new URLSearchParams(window.location.search);
    this.isGlassVariant = urlParams.has("autoCheckout");

    // Force dark theme immediately if glass variant is active
    if (this.isGlassVariant) {
      this.forceGlassVariantTheme();
    }

    this.checkForOAuthError();
    this.handleOAuthReturn();
  }

  override disconnectedCallback() {
    // Restore theme before unmounting
    this.restoreOriginalTheme();

    super.disconnectedCallback();
  }

  private forceGlassVariantTheme() {
    // Only force if not already forced
    if (this.hasThemeBeenForced) {
      return;
    }

    // Store current theme preference from localStorage (not the resolved theme)
    this.originalTheme = getTheme();
    const resolvedTheme = getResolvedTheme();

    // Only force dark mode if not already dark
    if (resolvedTheme !== "dark") {
      // Set theme attribute directly without touching localStorage
      // This allows temporary override while preserving user's saved preference
      document.documentElement.setAttribute("data-theme", "dark");
      this.hasThemeBeenForced = true;
    }
  }

  private restoreOriginalTheme() {
    // Only restore if we previously forced the theme
    if (!this.hasThemeBeenForced || this.originalTheme === null) {
      return;
    }

    // Restore by re-applying the user's saved theme from localStorage
    // This will set the correct data-theme attribute based on their preference
    setTheme(this.originalTheme);

    // Reset state
    this.originalTheme = null;
    this.hasThemeBeenForced = false;
  }

  override firstUpdated() {
    // Handle guest checkout params after form is rendered
    this.handleGuestCheckoutParams();
  }

  private handleGuestCheckoutParams() {
    if (this.isFormInitialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const prefilledEmail = urlParams.get("email");
    const returnUrl = urlParams.get("return");
    const autoCheckout = urlParams.get("autoCheckout");

    this.isPurchaseMode = !!autoCheckout;

    // Only store returnUrl if it passes security validation
    if (returnUrl && isValidRedirectUrl(returnUrl)) {
      sessionStorage.setItem("postLoginReturn", returnUrl);
    }

    if (prefilledEmail) {
      // Use form controller's reset method to initialize with prefilled email
      // This happens after firstUpdated, so the form element exists
      this.formController.reset({
        email: prefilledEmail,
        password: "",
      });
      this.isFormInitialized = true;
    }
  }

  private checkForOAuthError() {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("error");

    if (error) {
      this.formError = "Sign-in failed, try later"; // `Authentication failed: ${decodeURIComponent(error)}`;

      // Clean up URL to remove error parameter
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }
  }

  private async handleOAuthReturn() {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthComplete = urlParams.get("oauthComplete");

    if (oauthComplete !== "true") return;

    // OAuth just completed - session cookie should have been set by the OAuth callback
    // Clean the oauthComplete param from URL while preserving checkout params
    urlParams.delete("oauthComplete");
    const newSearch = urlParams.toString();
    const cleanUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
    window.history.replaceState(null, "", cleanUrl);

    // Set purchase mode for UI
    const autoCheckout = urlParams.get("autoCheckout");
    this.isPurchaseMode = !!autoCheckout;

    // Wait for session initialization to complete (auth service attempts session auth on startup)
    const authState = resolve<IAuthState>("authState");
    await authState.ready;

    if (authState.isAuthenticated.get()) {
      // Process auto-checkout with the URL params
      await this.processAutoCheckout(urlParams);
    }
  }

  private async processAutoCheckout(urlParams: URLSearchParams) {
    const autoCheckout = urlParams.get("autoCheckout");
    const eventId = urlParams.get("eventId");
    const contentId = urlParams.get("contentId");
    const rawReturnUrl = urlParams.get("return");

    // Validate returnUrl to prevent open redirect attacks
    const returnUrl = rawReturnUrl && isValidRedirectUrl(rawReturnUrl) ? rawReturnUrl : null;

    // Handle post/link auto-checkout
    if (returnUrl && (autoCheckout === "post" || autoCheckout === "link") && contentId) {
      try {
        const checkoutResponse = await rpc("create-one-time-checkout", {
          contentType: autoCheckout as "post" | "link",
          contentId: contentId,
          returnUrl: returnUrl,
        });
        window.location.href = checkoutResponse.url;
        return;
      } catch (error) {
        console.error("Auto-checkout error:", error);
        window.location.href = returnUrl;
        return;
      }
    }

    // Handle live-event auto-checkout
    // Redirect to event page with autoPurchase flag - let event page handle username check
    if (returnUrl && autoCheckout === "live-event" && eventId) {
      const redirectUrl = new URL(returnUrl, window.location.origin);
      redirectUrl.searchParams.set("autoPurchase", "true");
      window.location.href = redirectUrl.toString();
      return;
    }

    // Check for guest checkout return URL (non-auto-checkout case)
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    // Default navigation to profile
    navigateTo("profile");
  }

  private get forgotPasswordHref(): string {
    const search = window.location.search;
    if (!search) return "forgot-password";
    const params = new URLSearchParams(search);
    if (!params.has("autoCheckout") && !params.has("return")) return "forgot-password";
    const returnTo = `/my/sign-in${search}`;
    return `forgot-password?returnTo=${encodeURIComponent(returnTo)}`;
  }

  private formController: WowFormController<SignInFormData> = new WowFormController(
    this,
    {
      email: "",
      password: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validateOnInputWhenDirty: false,
      validators: {
        email: [
          (value: string) => {
            if (!value) {
              return translate("sign-in.emailRequired");
            }
            if (!/\S+@\S+\.\S+/.test(value)) {
              return translate("sign-in.invalidEmail");
            }
            return;
          },
        ],
        password: [
          (value: string) => {
            if (!value) return translate("sign-in.passwordRequired");
            if (value.length < 6) return translate("sign-in.passwordMinLength");
            return;
          },
        ],
      },
    },
    async (event) => {
      this.formError = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;
      if (!isValid) {
        this.formError = translate("sign-in.fixErrors");
        return;
      }

      try {
        // IMPORTANT: Capture URL params BEFORE signInWithCredentials.
        // The router-auth-connector's onSignIn callback fires synchronously when auth state
        // changes and calls navigateTo(), which uses history.pushState and clears query params.
        const urlParams = new URLSearchParams(window.location.search);
        const autoCheckout = urlParams.get("autoCheckout");
        const eventId = urlParams.get("eventId");
        const contentId = urlParams.get("contentId");
        // Try sessionStorage first, fall back to URL param
        const storedReturnUrl = sessionStorage.getItem("postLoginReturn");
        const urlReturnParam = urlParams.get("return");
        const rawReturnUrl = storedReturnUrl || urlReturnParam;
        // Validate returnUrl to prevent open redirect attacks
        const isValid = rawReturnUrl ? isValidRedirectUrl(rawReturnUrl) : false;
        const returnUrl = rawReturnUrl && isValid ? rawReturnUrl : null;

        // Determine if we have a pending auto-checkout. If so, set a flag to prevent
        // router-auth-connector from navigating away before we complete the checkout.
        const hasAutoCheckout =
          (returnUrl && autoCheckout === "live-event" && eventId) ||
          (returnUrl && (autoCheckout === "post" || autoCheckout === "link") && contentId);

        if (hasAutoCheckout) {
          sessionStorage.setItem("pendingAutoCheckout", "true");
        }

        const authService = resolve<IAuthService>("authService");
        const result = await authService.signInWithCredentials(event.data.email.toLowerCase(), data.password);

        if (!result) {
          sessionStorage.removeItem("pendingAutoCheckout");
          this.formError = translate("sign-in.invalidCredentials");
        } else {
          // Handle post/link auto-checkout
          if (returnUrl && (autoCheckout === "post" || autoCheckout === "link") && contentId) {
            sessionStorage.removeItem("postLoginReturn");
            sessionStorage.removeItem("pendingAutoCheckout");
            try {
              const checkoutResponse = await rpc("create-one-time-checkout", {
                contentType: autoCheckout as "post" | "link",
                contentId: contentId,
                returnUrl: returnUrl,
              });
              window.location.href = checkoutResponse.url;
              return;
            } catch (error) {
              console.error("Auto-checkout error:", error);
              window.location.href = returnUrl;
              return;
            }
          }

          // Handle live-event auto-checkout
          // Redirect to event page with autoPurchase flag - let event page handle username check
          if (returnUrl && autoCheckout === "live-event" && eventId) {
            sessionStorage.removeItem("postLoginReturn");
            sessionStorage.removeItem("pendingAutoCheckout");

            // Redirect to event page with autoPurchase flag
            // The event page will check if user needs to set display name before checkout
            const redirectUrl = new URL(returnUrl, window.location.origin);
            redirectUrl.searchParams.set("autoPurchase", "true");
            window.location.href = redirectUrl.toString();
            return;
          }

          // Clear pending auto-checkout flag since we're not doing auto-checkout
          sessionStorage.removeItem("pendingAutoCheckout");

          // Check for guest checkout return URL (non-auto-checkout case)
          if (returnUrl) {
            sessionStorage.removeItem("postLoginReturn");
            window.location.href = returnUrl;
            return;
          }

          // Check for pending checkout
          const checkoutData = sessionStorage.getItem("pendingCheckout");

          if (checkoutData) {
            // Process pending checkout for returning users
            const out = await processPendingCheckout();
            this.isProcessingCheckout = out!.isProcessingCheckout;
            this.checkoutError = out!.checkoutError;
          } else {
            // Conditional navigation based on user type
            if (result.id && isFan(result)) {
              // Fan user: has name and id, but no handle/avatar - redirect to feed page
              window.location.href = "/feed";
            } else {
              // Creator user: has handle and avatar - stay in backoffice/profile page
              navigateTo("profile");
            }
          }
        }
      } catch (error) {
        // Clear pending auto-checkout flag on error
        sessionStorage.removeItem("pendingAutoCheckout");
        this.formError = translate("sign-in.generalError");
        console.error("Sign in error:", error);
      }
    },
  );

  private getTilePadding(row: number) {
    if (row < 2) {
      return `margin: 0 ${200 - row * 100}px 0 auto`;
    }

    if (row > 2) {
      return `margin: 0 auto 0 ${row * 100 - 200}px`;
    }

    return "margin: 0 auto";
  }

  private renderBackgroundTiles() {
    const renderTile = ({ handle, image }: { handle: string; image: string }) => html`
      <wow-creator-tile handle=${handle} mediaUrl=${image}></wow-creator-tile>
    `;

    const renderRow = (row: number) => {
      const padding = this.getTilePadding(row);
      return html` <div class="tile-row" style=${padding}>${this.creators[row]?.map(renderTile)}</div> `;
    };

    return html`
      <div class="page-background">
        <div class="background-tiles">
          ${renderRow(0)} ${renderRow(1)} ${renderRow(2)} ${renderRow(3)} ${renderRow(4)}
        </div>
      </div>
    `;
  }

  private renderSignInContent() {
    return html`
      <div class="main-content">
        <div class="header">
          <div class="title-section">
            <h1 class="title">${this.isPurchaseMode ? t("sign-in.purchaseTitle") : t("sign-in.title")}</h1>
            ${this.isPurchaseMode ? nothing : html`<p class="subtitle">${t("sign-in.subtitle")}</p>`}
          </div>
          <div class="social-buttons">
            <google-login-button></google-login-button>
            <!-- <apple-login-button></apple-login-button> -->
            <facebook-login-button></facebook-login-button>
          </div>
          <wow-divider>${t("sign-in.or")}</wow-divider>
        </div>
        <form id="sign-in-form">
          <div class="form-section">
            <wow-input
              name=${this.formController.fieldNames.email}
              type="email"
              placeholder=${t("sign-in.email")}
              autocomplete="email"
              required
            >
            </wow-input>
            <wow-input
              name=${this.formController.fieldNames.password}
              type="password"
              placeholder=${t("sign-in.password")}
              autocomplete="current-password"
              required
            ></wow-input>
            <p style="text-align: right; margin: 0;">
              <wow-link href=${this.forgotPasswordHref}>${t("sign-in.forgotPassword")}</wow-link>
            </p>
            ${this.formError ? html`<div class="form-error">${this.formError}</div>` : ""}
            <wow-button form="sign-in-form" class="submit-button" type="submit">${t("sign-in.signIn")}</wow-button>
          </div>
        </form>
      </div>
      <div class="footer">
        ${this.isPurchaseMode
          ? nothing
          : html`<p>
              ${t("sign-in.noAccount")}

              <wow-link href="sign-up"> ${t("sign-in.signUp")} </wow-link>
            </p>`}
        <p class="terms-text">
          ${t("sign-in.termsAndConditions")}
          <a href=${TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer" style="color: inherit;"
            >${t("sign-in.termsOfServiceLink")}</a
          >
          ${t("sign-in.and")}
          <a href=${PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style="color: inherit;"
            >${t("sign-in.privacyPolicyLink")}</a
          >
          ${t("sign-in.byWorldOfWow")}
        </p>
      </div>
    `;
  }

  protected override render() {
    if (this.isProcessingCheckout) {
      return html`
        <div class="container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="main-content" style="text-align: center; justify-content: center;">
            <h2 class="title">Processing your purchase...</h2>
            <p class="subtitle">Please wait while we redirect you to checkout.</p>
            <ph-circle-notch
              size="48px"
              style="animation: spin 1s linear infinite; margin: 24px auto;"
            ></ph-circle-notch>
          </div>
        </div>
      `;
    }

    if (this.checkoutError) {
      return html`
        <div class="container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="main-content">
            <h2 class="title">Checkout Error</h2>
            <p style="color: var(--color-error, #dc2626); margin: 16px 0;">${this.checkoutError}</p>
            <wow-button
              @click=${() => {
                this.checkoutError = "";
                void processPendingCheckout();
              }}
            >
              Try Again
            </wow-button>
            <wow-button href="/feed" variant="neutral" style="margin-top: 12px;"> Continue to WOW </wow-button>
          </div>
        </div>
      `;
    }

    if (this.isGlassVariant) {
      return html`
        <div class="glass-page">
          ${this.renderBackgroundTiles()}

          <div class="logo">
            <wow-logo></wow-logo>
          </div>

          <div class="glass-modal-overlay">
            <div class="glass-modal-container">${this.renderSignInContent()}</div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="container">
        <div class="logo">
          <wow-logo></wow-logo>
        </div>

        ${this.renderSignInContent()}
      </div>
    `;
  }
}
